import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { UNIT_SYSTEMS, getDefaultUnits, getUnitDefaults, getThicknessTolerance, formatLayoutLabel } from '../utils/units';
import { uid } from '../utils/uid';

const CutListContext = createContext(null);

const STORAGE_KEY = 'cutlist-state';

// Available optimization algorithms
export const ALGORITHMS = {
  BOTTOM_LEFT: 'bottom-left',      // Free
  SKYLINE: 'skyline',              // Pro
  FFDH: 'ffdh',                    // Pro
  MAXIMAL_RECTANGLES: 'maximal-rectangles', // Pro
  BEST_RESULT: 'best-result',      // Pro - runs all 4 × 5 sorts, picks best
};

// Shape types for pieces
export const SHAPE_TYPES = {
  RECTANGLE: 'rectangle',
  TRAPEZOID: 'trapezoid',
  POLYGON: 'polygon',
};

export const ALGORITHM_INFO = {
  [ALGORITHMS.BOTTOM_LEFT]: {
    name: 'Bottom-Left',
    description: 'Fast placement • Free',
    efficiency: '75-85%',
    speed: 'Instant',
    supportsPolygons: false,
  },
  [ALGORITHMS.SKYLINE]: {
    name: 'Skyline',
    description: 'Gap-filling placement • Pro',
    efficiency: '80-90%',
    speed: 'Fast',
    supportsPolygons: false,
  },
  [ALGORITHMS.FFDH]: {
    name: 'FFDH',
    description: 'Smart shelf packing • Pro',
    efficiency: '80-88%',
    speed: 'Fast',
    supportsPolygons: false,
  },
  [ALGORITHMS.MAXIMAL_RECTANGLES]: {
    name: 'MaxRects',
    description: 'Optimal rectangular packing • Pro',
    efficiency: '85-92%',
    speed: 'Medium',
    supportsPolygons: false,
  },
  [ALGORITHMS.BEST_RESULT]: {
    name: 'Best Result',
    description: 'Runs all algorithms + deep search, picks the best • Pro',
    efficiency: '88-96%',
    speed: '~3-5 seconds',
    supportsPolygons: false,
  },
};

// Storage version for migration
const STORAGE_VERSION = 3;

export const CutListProvider = ({ children }) => {
  // Initialize units from locale
  const initialUnits = getDefaultUnits();
  const defaults = getUnitDefaults(initialUnits.unit, initialUnits.system);

  // Core state
  const [pieces, setPieces] = useState([]);

  // Stock sheet types - explicit material types with dimensions
  // Each type has: id, name, width, length, thickness
  const [stockSheetTypes, setStockSheetTypes] = useState([]);

  // Legacy fallback sheet size (for unassigned pieces)
  const [sheetWidth, setSheetWidth] = useState(defaults.sheetW);
  const [sheetLength, setSheetLength] = useState(defaults.sheetL);

  const [cutWidth, setCutWidth] = useState(defaults.kerf);
  const [trimFactoryEdges, setTrimFactoryEdges] = useState(false);
  const [trimAmount, setTrimAmount] = useState(0);
  const [currentUnits, setCurrentUnits] = useState(initialUnits);
  const [algorithm, setAlgorithm] = useState(ALGORITHMS.BOTTOM_LEFT);

  // Optimization results
  const [layouts, setLayouts] = useState([]);
  const [stats, setStats] = useState({});

  // UI state
  const [errors, setErrors] = useState([]);
  const [highlightedPieceId, setHighlightedPieceId] = useState(null);

  // Stock sheet type management
  const addStockSheetType = useCallback(({ name, width, length, thickness }) => {
    const defs = getUnitDefaults(currentUnits.unit, currentUnits.system);
    const newType = {
      id: uid(),
      name: name || `Stock ${stockSheetTypes.length + 1}`,
      width: width || defs.sheetW,
      length: length || defs.sheetL,
      thickness: thickness || 0
    };
    setStockSheetTypes(prev => [...prev, newType]);
    return newType.id;
  }, [stockSheetTypes.length, currentUnits]);

  const updateStockSheetType = useCallback((id, updates) => {
    setStockSheetTypes(prev => prev.map(type =>
      type.id === id ? { ...type, ...updates } : type
    ));
  }, []);

  const removeStockSheetType = useCallback((id) => {
    const deletedType = stockSheetTypes.find(t => t.id === id);
    const affectedPieces = pieces.filter(p => p.stockSheetTypeId === id);
    const remainingTypes = stockSheetTypes.filter(t => t.id !== id);

    if (affectedPieces.length > 0) {
      // If deleting an "Unassigned" sheet, just unassign pieces (set to null)
      // This prevents infinite loop of creating new Unassigned sheets
      if (deletedType?.name === 'Unassigned') {
        setStockSheetTypes(remainingTypes);
        setPieces(prev => prev.map(piece =>
          piece.stockSheetTypeId === id ? { ...piece, stockSheetTypeId: null } : piece
        ));
        return;
      }

      const defs = getUnitDefaults(currentUnits.unit, currentUnits.system);
      const deletedThickness = deletedType ? deletedType.thickness : defs.thickness;

      // Check if an existing "Unassigned" sheet with the same thickness exists
      const existingUnassigned = remainingTypes.find(
        t => t.name === 'Unassigned' && t.thickness === deletedThickness
      );

      if (existingUnassigned) {
        // Merge pieces into existing unassigned sheet
        setStockSheetTypes(remainingTypes);
        setPieces(prev => prev.map(piece =>
          piece.stockSheetTypeId === id ? { ...piece, stockSheetTypeId: existingUnassigned.id } : piece
        ));
      } else {
        // Create a new "Unassigned" sheet with the deleted type's thickness
        const defaultId = uid();
        const newDefault = {
          id: defaultId,
          name: 'Unassigned',
          width: defs.sheetW,
          length: defs.sheetL,
          thickness: deletedThickness,
        };
        setStockSheetTypes([...remainingTypes, newDefault]);
        setPieces(prev => prev.map(piece =>
          piece.stockSheetTypeId === id ? { ...piece, stockSheetTypeId: defaultId } : piece
        ));
      }
    } else {
      setStockSheetTypes(remainingTypes);
    }
  }, [pieces, stockSheetTypes]);

  // Get stock type for a piece (explicit assignment or auto-match by thickness)
  const getStockTypeForPiece = useCallback((piece) => {
    if (piece.stockSheetTypeId) {
      return stockSheetTypes.find(t => t.id === piece.stockSheetTypeId);
    }
    // Auto-match by thickness
    const tol = getThicknessTolerance(currentUnits.unit, currentUnits.system);
    return stockSheetTypes.find(t =>
      Math.abs(t.thickness - (piece.thickness || 0)) < tol
    );
  }, [stockSheetTypes, currentUnits]);

  // Get sheet dimensions for a piece
  const getSheetSizeForPiece = useCallback((piece) => {
    const stockType = getStockTypeForPiece(piece);
    if (stockType) {
      return { width: stockType.width, length: stockType.length };
    }
    return { width: sheetWidth, length: sheetLength };
  }, [getStockTypeForPiece, sheetWidth, sheetLength]);

  // Clear localStorage on mount - start fresh each visit
  // (localStorage persistence disabled for production)
  useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Failed to clear saved state:', e);
    }
  }, []);

  // Persist state to localStorage (disabled - app starts fresh each visit)
  // useEffect(() => {
  //   try {
  //     const stateToSave = {
  //       version: STORAGE_VERSION,
  //       pieces,
  //       stockSheetTypes,
  //       sheetWidth,
  //       sheetHeight,
  //       cutWidth,
  //       trimFactoryEdges,
  //       trimAmount,
  //       currentUnits,
  //       algorithm,
  //     };
  //     localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  //   } catch (e) {
  //     console.error('Failed to save state:', e);
  //   }
  // }, [pieces, stockSheetTypes, sheetWidth, sheetHeight, cutWidth, trimFactoryEdges, trimAmount, currentUnits, algorithm]);

  // Error management
  const addError = useCallback((message) => {
    console.error(`[CutList Error]: ${message}`);
    const error = { id: uid(), message, timestamp: new Date().toLocaleTimeString() };
    setErrors(prev => [...prev, error]);
    setTimeout(() => {
      setErrors(prev => prev.filter(e => e.id !== error.id));
    }, 5000);
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  // Unit handling
  const handleUnitChange = useCallback(({ system, unit }) => {
    setCurrentUnits({ system, unit });

    // Update unassigned/default sheet dimensions to match the new unit
    const defs = getUnitDefaults(unit, system);
    setSheetWidth(defs.sheetW);
    setSheetLength(defs.sheetL);
    setCutWidth(defs.kerf);
  }, []);

  // Recompute layout thicknessLabel when units change
  useEffect(() => {
    setLayouts(prev => {
      if (prev.length === 0) return prev;
      return prev.map(layout => ({
        ...layout,
        thicknessLabel: formatLayoutLabel(layout, currentUnits.unit, currentUnits.system)
      }));
    });
  }, [currentUnits.unit, currentUnits.system]);

  // Piece management
  const addPiece = useCallback((piece = null, stockSheetTypeId = null) => {
    if (piece) {
      setPieces(prev => [...prev, {
        ...piece,
        id: piece.id || uid(),
        stockSheetTypeId: piece.stockSheetTypeId !== undefined ? piece.stockSheetTypeId : stockSheetTypeId
      }]);
    } else {
      const defs = getUnitDefaults(currentUnits.unit, currentUnits.system);

      setPieces(prev => [...prev, {
        id: uid(),
        name: `Part ${prev.length + 1}`,
        width: defs.pieceW,
        length: defs.pieceL,
        quantity: 1,
        material: 'Plywood',
        thickness: defs.thickness,
        grainDirection: 'none',
        shapeType: SHAPE_TYPES.RECTANGLE,
        vertices: null,
        stockSheetTypeId: stockSheetTypeId
      }]);
    }
  }, [currentUnits]);

  const updatePiece = useCallback((id, field, value) => {
    // Find the piece being updated
    const piece = pieces.find(p => p.id === id);
    if (!piece) return false;

    // Get sheet size for this piece
    const { width: pieceSheetWidth, length: pieceSheetLength } = getSheetSizeForPiece(piece);
    const maxDimension = Math.max(pieceSheetWidth, pieceSheetLength);

    // Validate dimensions
    if (field === 'width' || field === 'length') {
      const numValue = parseFloat(value);
      if (numValue <= 0) {
        addError(`${field === 'width' ? 'Width' : 'Length'} must be greater than 0`);
        return false;
      }
      if (numValue > maxDimension) {
        addError(`${field === 'width' ? 'Width' : 'Length'} cannot exceed sheet size (${maxDimension.toFixed(2)})`);
        return false;
      }
    }

    setPieces(prev => prev.map(p =>
      p.id === id ? { ...p, [field]: value } : p
    ));
    return true;
  }, [pieces, getSheetSizeForPiece, currentUnits, addError]);

  const removePiece = useCallback((id) => {
    setPieces(prev => prev.filter(piece => piece.id !== id));
  }, []);

  const addPieces = useCallback((newPieces) => {
    setPieces(prev => [...prev, ...newPieces]);
  }, []);

  const clearPieces = useCallback(() => {
    setPieces([]);
  }, []);

  // Reset everything
  const reset = useCallback(() => {
    setPieces([]);
    setLayouts([]);
    setStats({});
    setHighlightedPieceId(null);
  }, []);

  // Full reset including settings
  const resetAll = useCallback(() => {
    reset();
    const defs = getUnitDefaults(currentUnits.unit, currentUnits.system);
    setSheetWidth(defs.sheetW);
    setSheetLength(defs.sheetL);
    setStockSheetTypes([]);
    setCutWidth(defs.kerf);
  }, [reset, currentUnits]);

  const value = {
    // Pieces
    pieces,
    setPieces,
    addPiece,
    updatePiece,
    removePiece,
    addPieces,
    clearPieces,

    // Stock sheet types
    stockSheetTypes,
    setStockSheetTypes,
    addStockSheetType,
    updateStockSheetType,
    removeStockSheetType,
    getStockTypeForPiece,
    getSheetSizeForPiece,

    // Legacy/fallback sheet config
    sheetWidth,
    setSheetWidth,
    sheetLength,
    setSheetLength,
    cutWidth,
    setCutWidth,
    trimFactoryEdges,
    setTrimFactoryEdges,
    trimAmount,
    setTrimAmount,

    // Units
    currentUnits,
    setCurrentUnits,
    handleUnitChange,

    // Algorithm
    algorithm,
    setAlgorithm,

    // Optimization results
    layouts,
    setLayouts,
    stats,
    setStats,

    // UI state
    errors,
    addError,
    clearErrors,
    highlightedPieceId,
    setHighlightedPieceId,

    // Actions
    reset,
    resetAll,
  };

  return (
    <CutListContext.Provider value={value}>
      {children}
    </CutListContext.Provider>
  );
};

export const useCutList = () => {
  const context = useContext(CutListContext);
  if (!context) {
    throw new Error('useCutList must be used within a CutListProvider');
  }
  return context;
};

export default CutListContext;
